import { useMemo, useState, useEffect, useRef, useCallback } from 'react'
import { fetchLibraryImages, updateLibraryImage, deleteLibraryImages } from 'api/library'
import { LIBRARY_TABS, parseSortKey } from '../../constant'

export function useLibraryList({
  activeTab,
  search = '',
  sort = null,
  ratios = [],
  isOpen = true,
}) {
  const [data, setData] = useState(null)
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)
  const [isFetchMoreError, setIsFetchMoreError] = useState(false)
  const [isFetchingMore, setIsFetchingMore] = useState(false)
  const [refetchCounter, setRefetchCounter] = useState(0)
  const skipPageLoadRef = useRef(false)
  const DEFAULT_LIMIT = 15
  const shouldFetch = isOpen && activeTab !== LIBRARY_TABS.SELECTED

  const requestFilters = useMemo(() => {
    const { sortBy, sortOrder } = sort
      ? parseSortKey(sort)
      : { sortBy: null, sortOrder: null }

    return {
      limit: DEFAULT_LIMIT,
      search: search || null,
      sort_by: sortBy,
      sort_order: sortOrder,
      ratio: ratios.length ? ratios : null,
      image_type: activeTab === LIBRARY_TABS.ALL || activeTab === LIBRARY_TABS.SELECTED ? null : activeTab,
    }
  }, [search, sort, ratios, activeTab])

  useEffect(() => {
    if (!shouldFetch) {
      return
    }

    setPage(1)
    setIsFetchMoreError(false)
    setIsError(false)
  }, [requestFilters, shouldFetch])

  useEffect(() => {
    if (!shouldFetch) {
      setIsLoading(false)
      return undefined
    }

    let isCancelled = false

    const loadImages = async () => {
      if (skipPageLoadRef.current) {
        skipPageLoadRef.current = false
        return
      }

      if (page === 1) {
        setIsError(false)
        setIsFetchMoreError(false)
        setIsLoading(true)
      } else {
        setIsFetchingMore(true)
      }

      const requestBody = {
        ...requestFilters,
        page,
      }

      const response = await fetchLibraryImages(requestBody)

      if (isCancelled) {
        return
      }

      if (response.success) {
        setData((prevData) => {
          if (page === 1 || !prevData) {
            return response.data
          }

          return {
            ...response.data,
            items: [...(prevData.items || []), ...(response.data.items || [])],
          }
        })
      } else if (page === 1) {
        setIsError(true)
      } else {
        setIsFetchMoreError(true)
        skipPageLoadRef.current = true
        setPage((prevPage) => prevPage - 1)
      }

      setIsLoading(false)
      setIsFetchingMore(false)
    }

    loadImages()

    return () => {
      isCancelled = true
    }
  }, [requestFilters, page, refetchCounter, shouldFetch])

  const refetch = useCallback(() => {
    setIsFetchMoreError(false)
    setIsError(false)
    setPage((prevPage) => {
      if (prevPage === 1) {
        setRefetchCounter((count) => count + 1)
      }
      return 1
    })
  }, [])

  const hasMore = Boolean(data?.nextPage)

  const fetchNextPage = useCallback(() => {
    if (isLoading || isFetchingMore || isFetchMoreError || !data?.nextPage) {
      return
    }

    setPage((prevPage) => prevPage + 1)
  }, [data?.nextPage, isFetchingMore, isFetchMoreError, isLoading])

  const retryFetchMore = useCallback(() => {
    if (!data?.nextPage) {
      return
    }

    setIsFetchMoreError(false)
    setPage((prevPage) => prevPage + 1)
  }, [data?.nextPage])

  const removeItemsByIds = useCallback((idsToRemove, removedVariantCountByGroup = new Map()) => {
    if (!idsToRemove?.size) {
      return
    }

    setData((prevData) => {
      if (!prevData?.items?.length) {
        return prevData
      }

      return {
        ...prevData,
        items: prevData.items
          .filter((item) => !idsToRemove.has(item.id))
          .map((item) => {
            const removedCount = removedVariantCountByGroup.get(item.group_id)
            if (!item.original || !removedCount) {
              return item
            }
            return {
              ...item,
              total_variations: Math.max(0, (item.total_variations ?? 0) - removedCount),
            }
          }),
      }
    })
  }, [])

  const renameImageById = async ({ imageId, imageName, groupId }) => {
    const response = await updateLibraryImage({
      image_id: imageId,
      image_name: imageName,
      group_id: groupId,
    })

    if (!response.success) {
      return response
    }

    setData((prevData) => {
      if (!prevData?.items?.length) {
        return prevData
      }

      return {
        ...prevData,
        items: prevData.items.map((item) => (
          item.id === imageId ? { ...item, image_name: imageName } : item
        )),
      }
    })

    return response
  }

  const deleteImageById = async ({ imageId, groupId, isOriginal }) => {
    const response = await deleteLibraryImages({
      [String(groupId)]: [imageId],
    })

    if (!response.success) {
      return response
    }

    const removedVariantCountByGroup = isOriginal ? new Map() : new Map([[groupId, 1]])
    removeItemsByIds(new Set([imageId]), removedVariantCountByGroup)

    return response
  }

  const deleteImagesByGroups = async (payload, removedVariantCountByGroup = new Map()) => {
    const response = await deleteLibraryImages(payload)

    if (!response.success) {
      return response
    }

    const imageIds = Object.values(payload ?? {}).flat()
    removeItemsByIds(new Set(imageIds), removedVariantCountByGroup)

    return response
  }

  return {
    data,
    isLoading,
    isError,
    isFetchMoreError,
    isFetchingMore,
    hasMore,
    hasFilters: Boolean(requestFilters.search || requestFilters.ratio),
    fetchNextPage,
    retryFetchMore,
    renameImageById,
    deleteImageById,
    deleteImagesByGroups,
    refetch,
  }
}
